<div
  class="labor-modal overflow-hidden bg-[var(--lr-bg)] text-[var(--lr-text)]"
  [attr.data-theme]="themeMode"
>
  <header class="flex items-center justify-between gap-4 border-b border-[var(--lr-border)] px-5 py-4">
    <div class="min-w-0">
      <p class="m-0 text-[10px] font-extrabold uppercase tracking-[0.18em] text-sky-500">Onsite labor</p>
      <div class="mt-1 flex flex-wrap items-center gap-2">
        <h2 class="m-0 truncate text-xl font-extrabold tracking-tight text-[var(--lr-text)]">{{ dialogTitle }}</h2>
        <span class="rounded-full border border-[var(--lr-border)] bg-[var(--lr-panel)] px-2.5 py-1 font-mono text-[11px] font-bold text-[var(--lr-muted)]">
          # {{ data.rowData['v_ladings.alternate_reference'] || data.lading_uuid }}
        </span>
      </div>
    </div>

    <button
      type="button"
      class="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-[var(--lr-border)] bg-[var(--lr-panel)] text-[var(--lr-muted)] transition hover:border-sky-500/50 hover:text-sky-500"
      aria-label="Close onsite time details"
      (click)="close()"
    >
      <i class="fa-solid fa-xmark"></i>
    </button>
  </header>

  <mat-dialog-content class="!m-0 !max-h-[78vh] !p-0">
    <div class="space-y-3 p-4 sm:p-5">
      <section class="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <div class="rounded-xl border border-sky-500/30 bg-sky-500/10 p-3">
          <span class="block text-[10px] font-extrabold uppercase tracking-[0.12em] text-sky-500">{{ stageLabel }} total</span>
          <strong class="mt-1 block text-xl font-black text-[var(--lr-text)]">{{ formatMinutes(totalMinutes) }}</strong>
        </div>
        <div class="rounded-xl border border-[var(--lr-border)] bg-[var(--lr-panel)] p-3">
          <span class="block text-[10px] font-extrabold uppercase tracking-[0.12em] text-[var(--lr-muted)]">Install</span>
          <strong class="mt-1 block text-xl font-black text-[var(--lr-text)]">{{ formatMinutes(installMinutes) }}</strong>
        </div>
        <div class="rounded-xl border border-[var(--lr-border)] bg-[var(--lr-panel)] p-3">
          <span class="block text-[10px] font-extrabold uppercase tracking-[0.12em] text-[var(--lr-muted)]">Handling</span>
          <strong class="mt-1 block text-xl font-black text-[var(--lr-text)]">{{ formatMinutes(handlingMinutes) }}</strong>
        </div>
        <div class="rounded-xl border border-[var(--lr-border)] bg-[var(--lr-panel)] p-3">
          <span class="block text-[10px] font-extrabold uppercase tracking-[0.12em] text-[var(--lr-muted)]">Crew</span>
          <strong class="mt-1 block text-xl font-black text-[var(--lr-text)]">{{ formatWholeNumber(people) }}</strong>
        </div>
      </section>

      <div class="flex flex-wrap items-center gap-2 text-[11px] font-bold">
        <span class="rounded-full border border-sky-500/30 bg-sky-500/10 px-2.5 py-1 text-sky-500">{{ formatStatus(review?.status) }}</span>
        <span class="rounded-full border border-[var(--lr-border)] bg-[var(--lr-panel)] px-2.5 py-1 text-[var(--lr-muted)]">Requested {{ formatTimestamp(review?.requested_at) }}</span>
        <span class="rounded-full border border-[var(--lr-border)] bg-[var(--lr-panel)] px-2.5 py-1 text-[var(--lr-muted)]">Approved {{ formatTimestamp(review?.approved_at) }}</span>
        <span *ngIf="isTechnicianOrder" class="rounded-full border border-violet-500/30 bg-violet-500/10 px-2.5 py-1 text-violet-400">Technician order</span>
      </div>

      <section
        class="grid gap-4 rounded-xl border p-4 lg:grid-cols-[minmax(180px,0.8fr)_minmax(420px,1.7fr)]"
        [ngClass]="{
          'border-[var(--lr-border)] bg-[var(--lr-panel)]': !isClockedIn,
          'border-amber-500/30 bg-amber-500/10': isClockRunning,
          'border-emerald-500/30 bg-emerald-500/10': isClockedOut && !wentOver,
          'border-rose-500/30 bg-rose-500/10': isClockedOut && wentOver
        }"
      >
        <div class="min-w-0 self-center">
          <span class="text-[10px] font-extrabold uppercase tracking-[0.14em] text-[var(--lr-muted)]">Actual onsite time</span>

          <ng-container *ngIf="!isClockedIn">
            <strong class="mt-1 block text-base font-extrabold">Not started</strong>
            <span class="mt-1 block text-xs text-[var(--lr-muted)]">Enter the actual start and finish.</span>
          </ng-container>

          <ng-container *ngIf="isClockRunning">
            <strong class="mt-1 block text-base font-extrabold">In progress · {{ formatMinutes(actualMinutes) }}</strong>
            <span class="mt-1 block text-xs text-[var(--lr-muted)]">Since {{ formatTimestamp(review?.actual_started_at) }}</span>
          </ng-container>

          <ng-container *ngIf="isClockedOut">
            <strong class="mt-1 block text-base font-extrabold">{{ formatMinutes(actualMinutes) }} actual</strong>
            <span *ngIf="allowedMinutes !== null && !wentOver" class="mt-1 block text-xs font-bold text-emerald-500">Within {{ formatMinutes(allowedMinutes) }} allowance</span>
            <span *ngIf="wentOver" class="mt-1 block text-xs font-bold text-rose-500">Over by {{ formatMinutes(overMinutes) }}</span>
            <span *ngIf="allowedMinutes === null" class="mt-1 block text-xs text-[var(--lr-muted)]">No allowance to compare</span>
          </ng-container>
        </div>

        <form [formGroup]="actualTimeForm" (ngSubmit)="saveActualTime()" class="grid items-end gap-2 sm:grid-cols-[1fr_1fr_auto]">
          <label class="block text-[11px] font-bold text-[var(--lr-muted)]">
            Started
            <input class="lr-control mt-1" type="datetime-local" step="60" formControlName="actual_started_at">
          </label>
          <label class="block text-[11px] font-bold text-[var(--lr-muted)]">
            Completed
            <input class="lr-control mt-1" type="datetime-local" step="60" formControlName="actual_completed_at">
          </label>
          <button class="lr-button lr-button--primary mr-1 h-[42px]" type="submit" [disabled]="actualTimeForm.invalid || savingActualTime || submitting">
            {{ savingActualTime ? 'Saving…' : 'Save times' }}
          </button>
          <p *ngIf="actualTimeForm.hasError('startRequired')" class="m-0 text-xs font-bold text-rose-500 sm:col-span-3">Enter a started time before the completed time.</p>
          <p *ngIf="actualTimeForm.hasError('completedBeforeStarted')" class="m-0 text-xs font-bold text-rose-500 sm:col-span-3">Completed time cannot be before started time.</p>
        </form>
      </section>

      <div *ngIf="parseError" class="flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs font-bold text-amber-500">
        <i class="fa-solid fa-triangle-exclamation"></i>
        Existing labor data could not be parsed. You can still submit a request.
      </div>

      <section class="rounded-xl border border-[var(--lr-border)] bg-[var(--lr-panel)] p-4">
        <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <span class="text-[10px] font-extrabold uppercase tracking-[0.14em] text-sky-500">Labor request</span>
            <h3 class="m-0 mt-0.5 text-base font-extrabold">{{ hasExistingRequest ? 'Update requested labor' : 'Request additional labor' }}</h3>
          </div>
          <div class="flex gap-2">
            <span class="rounded-lg border border-[var(--lr-border)] bg-[var(--lr-bg)] px-2.5 py-1.5 text-xs font-bold text-[var(--lr-muted)]">{{ formatMinutes(requestedTotal) }} total</span>
            <span class="rounded-lg border border-[var(--lr-border)] bg-[var(--lr-bg)] px-2.5 py-1.5 text-xs font-bold text-[var(--lr-muted)]">{{ formatWholeNumber(requestForm.get('requested_people')?.value) }} crew</span>
          </div>
        </div>

        <form [formGroup]="requestForm" (ngSubmit)="submitRequest()" class="space-y-3">
          <div class="grid gap-2 sm:grid-cols-3">
            <label class="block text-[11px] font-bold text-[var(--lr-muted)]">
              Install minutes
              <input class="lr-control mt-1" type="number" min="0" step="1" formControlName="requested_install_minutes">
            </label>
            <label class="block text-[11px] font-bold text-[var(--lr-muted)]">
              Handling minutes
              <input class="lr-control mt-1" type="number" min="0" step="1" formControlName="requested_handling_minutes">
            </label>
            <label class="block text-[11px] font-bold text-[var(--lr-muted)]">
              Crew
              <input class="lr-control mt-1" type="number" min="0" step="1" placeholder="No change" formControlName="requested_people" (blur)="coerceRequestedPeople()">
            </label>
          </div>

          <div *ngIf="isTechnicianOrder" class="rounded-xl border border-violet-500/25 bg-violet-500/10 p-3">
            <div class="mb-2 flex items-center gap-2">
              <i class="fa-solid fa-screwdriver-wrench text-xs text-violet-400"></i>
              <span class="text-[10px] font-extrabold uppercase tracking-[0.14em] text-violet-400">Technician travel</span>
              <span class="text-[11px] text-[var(--lr-muted)]">Separate from onsite time</span>
            </div>
            <div class="grid gap-2 sm:grid-cols-2">
              <label class="block text-[11px] font-bold text-[var(--lr-muted)]">
                Drive time (hours)
                <input class="lr-control mt-1" type="number" min="0" step="0.25" placeholder="None" formControlName="requested_technician_drive_hours">
              </label>
              <label class="block text-[11px] font-bold text-[var(--lr-muted)]">
                Hotel
                <select class="lr-control mt-1" formControlName="requested_technician_hotel_nights">
                  <option *ngFor="let option of hotelNightOptions" [ngValue]="option.value">{{ option.label }}</option>
                </select>
              </label>
            </div>
          </div>

          <label class="block text-[11px] font-bold text-[var(--lr-muted)]">
            Request notes
            <textarea class="lr-control mt-1 min-h-[64px] resize-y" rows="2" formControlName="requested_notes" placeholder="Access issues, extra handling, travel details, or other context"></textarea>
          </label>

          <p *ngIf="requestForm.get('requested_people')?.hasError('wholeNumber')" class="m-0 text-xs font-bold text-rose-500">Crew must be a whole number.</p>

          <div class="flex items-center justify-end gap-2 pt-0.5">
            <button class="lr-button lr-button--secondary" type="button" (click)="resetForm()" [disabled]="submitting">Reset</button>
            <button class="lr-button lr-button--primary" type="submit" [disabled]="requestForm.invalid || submitting">{{ submitLabel }}</button>
          </div>
        </form>
      </section>

      <details *ngIf="review" class="group rounded-xl border border-[var(--lr-border)] bg-[var(--lr-panel)]">
        <summary class="flex cursor-pointer list-none items-center justify-between px-4 py-3 text-xs font-extrabold text-[var(--lr-muted)]">
          <span>Estimate, request, and approval breakdown</span>
          <i class="fa-solid fa-chevron-down text-[10px] transition-transform group-open:rotate-180"></i>
        </summary>

        <div class="grid gap-2 border-t border-[var(--lr-border)] p-3 md:grid-cols-3">
          <section class="lr-breakdown-card">
            <h4>Estimated</h4>
            <dl>
              <div><dt>Total</dt><dd>{{ formatMinutes(review.estimated_total_minutes) }}</dd></div>
              <div><dt>Install</dt><dd>{{ formatMinutes(review.estimated_install_minutes) }}</dd></div>
              <div><dt>Handling</dt><dd>{{ formatMinutes(review.estimated_handling_minutes) }}</dd></div>
              <div><dt>Crew</dt><dd>{{ formatWholeNumber(review.estimated_people) }}</dd></div>
            </dl>
          </section>

          <section class="lr-breakdown-card">
            <h4>Requested</h4>
            <dl>
              <div><dt>Total</dt><dd>{{ formatMinutes(review.requested_total_minutes) }}</dd></div>
              <div><dt>Install</dt><dd>{{ formatMinutes(review.requested_install_minutes) }}</dd></div>
              <div><dt>Handling</dt><dd>{{ formatMinutes(review.requested_handling_minutes) }}</dd></div>
              <div><dt>Crew</dt><dd>{{ formatWholeNumber(review.requested_people) }}</dd></div>
              <div *ngIf="isTechnicianOrder"><dt>Drive</dt><dd>{{ formatHours(review.requested_technician_drive_hours) }}</dd></div>
              <div *ngIf="isTechnicianOrder"><dt>Hotel</dt><dd>{{ formatHotelNights(review.requested_technician_hotel_nights) }}</dd></div>
            </dl>
          </section>

          <section class="lr-breakdown-card">
            <h4>Approved</h4>
            <dl>
              <div><dt>Total</dt><dd>{{ formatMinutes(review.approved_total_minutes) }}</dd></div>
              <div><dt>Install</dt><dd>{{ formatMinutes(review.approved_install_minutes) }}</dd></div>
              <div><dt>Handling</dt><dd>{{ formatMinutes(review.approved_handling_minutes) }}</dd></div>
              <div><dt>Crew</dt><dd>{{ formatWholeNumber(review.approved_people) }}</dd></div>
            </dl>
          </section>
        </div>

        <div class="flex flex-wrap gap-x-4 gap-y-1 border-t border-[var(--lr-border)] px-4 py-2 text-[10px] text-[var(--lr-muted)]">
          <span>Created {{ formatTimestamp(review.created) }}</span>
          <span>Updated {{ formatTimestamp(review.updated) }}</span>
          <span class="font-mono">{{ review.lading_labour_review_uuid }}</span>
        </div>
      </details>
    </div>
  </mat-dialog-content>
</div>
